import React from 'react'
import { renderToString } from 'react-dom/server'
import Helmet from 'react-helmet'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Theme from 'components/common/Theme/Theme'
import Media from 'components/common/Media'
import Mixin from 'components/common/Mixin'
import { LazyDiv } from 'components/common/LazyHtml'
import { generateAdaptiveContent, generateContentLimit } from './helpers'
import { ToggleLink } from 'components/common/Link/Link'
import { SPEAKABLE_SUMMARY } from 'constant'
import BrandsSchemasHelmet from 'components/BrandLandingPage/components/BrandsSchemasHelmet'
import ShopByTireSizeStatic from 'components/ShopByTireSizeStatic'
import FixedNotification from '../FixedNotification'


const BrandDetail = ({ backgroundImage,
  details: {
    brandLogoUrl,
    name,
    description,
    heroImageURL,
    largeDescription = false
  },
  expanded,
  heading,
  schemaUrl,
  toggleOnClick,
  hasActiveVehicle
}) =>
  <>
    <Media.Desktop>
      { heroImageURL && <HeroContainer url={ heroImageURL } /> }
    </Media.Desktop>
    <Background data-at-brand-detail url={ backgroundImage }>
      <BrandsSchemasHelmet />

      <Helmet>
        <script type="application/ld+json">
          {
            JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Brand',
              name,
              url: schemaUrl,
              logo: brandLogoUrl
            })
          }
        </script>
      </Helmet>

      <ContentWrapper>
        <DescriptionWrapper>
          <Media.Desktop>
            <Logo data-at-brand-detail-logo alt={ name } src={ `${brandLogoUrl}?$BOT-TireResultByBrand-Brand-Desktop-h80$` } />
          </Media.Desktop>
          <Media.Mobile>
            <Logo data-at-brand-detail-logo alt={ name } src={ `${brandLogoUrl}?$BOT-TireResultByBrand-Brand-Mobile-h60$` } />
          </Media.Mobile>
          {description && <Description data-at-brand-detail-description>
            <LazyDiv
              data-html-block
              content={ generateAdaptiveContent({
                className: SPEAKABLE_SUMMARY,
                description,
                limit: generateContentLimit(largeDescription),
                expanded,
                component: renderToString(
                  <CustomToggleLink
                    tabIndex="0"
                    role="button"
                    aria-expanded={ expanded }>
                    {expanded ? 'Read Less' : 'Read More'}
                  </CustomToggleLink>)
              }) }
              onClick={ toggleOnClick } />
          </Description>
          }
          <Heading data-at-brand-detail-heading>{ heading }</Heading>
        </DescriptionWrapper>

        <ShopByTireSizeSelector />
      </ContentWrapper>

    </Background>

    {!hasActiveVehicle && <FixedNotification />}
  </>
BrandDetail.propTypes = {
  backgroundImage: PropTypes.string,
  details: PropTypes.shape({
    brandLogoUrl: PropTypes.string,
    description: PropTypes.string,
    heroImageURL: PropTypes.string,
    largeDescription: PropTypes.bool,
    name: PropTypes.string
  }),
  expanded: PropTypes.bool,
  hasActiveVehicle: PropTypes.bool,
  heading: PropTypes.string,
  schemaUrl: PropTypes.string,
  toggleOnClick: PropTypes.func
}

export default BrandDetail

const Background = styled.div`
  background: url(${props => props.url}) no-repeat;
  background-size: cover;
  ${Mixin.responsive('padding', '20px 0 18px', '15px 16px 25px')}
`

const ContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  ${Media.mobile`
    flex-direction: column-reverse;
  `}
`

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  ${Media.mobile`
    width: 100%;
    align-items: center;
  `}
`

const Logo = styled.img`
  ${Mixin.responsive('max-width', '264px', '198px')}
`

const Heading = styled.div`
  max-width: 732px;
  margin: 12px 0 0;
  ${Mixin.font.GothamBold(18, 600, 24)}
  text-align: start;
`

const Description = styled.div`
  max-width: 732px;
  margin: 10px 0 0;
  line-height: 22px;
  color: ${Theme.colors.dark_grey};
  overflow: hidden;
  text-align: start;
`

const HeroContainer = styled.div`
  background: url(${props => props.url}) no-repeat;
  background-size: cover;
  height: 400px;
  margin-bottom: 20px;
  ${Media.mobile`
    height: 200px;
  `}
`

const CustomToggleLink = styled(ToggleLink)`
  cursor: pointer;
  margin-left: 5px;
`

const ShopByTireSizeSelector = styled(ShopByTireSizeStatic)`
  height: fit-content;
  ${Media.mobile`
    padding: 0;
    margin-bottom: 50px;
  `}
`
